/**
 * HandoffRulesPanel — AI Chat Handoff Rules management panel.
 *
 * Rendered as a tab inside the AI Agents page (/admin/ai-agents).
 * Allows administrators to configure rules for transferring unanswerable
 * AI chats to human agents on WhatsApp, Facebook, Instagram, Telegram, and Web Chat:
 *   - Priority and Channel routing
 *   - Specific target agents, teams, and skills allow-lists
 *   - Maximum agents to try and offer popup timeout
 *   - Custom customer handoff and queue messages
 */
import { useCallback, useEffect, useRef, useState, type JSX } from 'react'
import {
  ArrowRightLeft,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Search,
  X,
  AlertTriangle,
  Info,
  Clock,
  Users,
  Shield,
  MessageSquare,
  Sparkles,
  ChevronDown,
} from 'lucide-react'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'
import RequirePermission from '../../auth/RequirePermission.js'
import { useConfirmDialog } from '../../ui/ConfirmDialog.js'
import Select from '../../ui/Select.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface HandoffRule {
  id: string
  name: string
  status: boolean
  priority: number
  channels: string[]
  scopeSenderHandles: string[]
  maxAgents: number
  offerTimeoutSeconds: number
  targetAgentUserIds: string[]
  targetTeamIds: string[]
  targetSkills: string[]
  handoffMessage: string | null
  noAcceptMessage: string | null
  createdAt: string
  updatedAt: string
}

export interface TargetAssignees {
  agents: Array<{ id: string; name: string; email: string }>
  teams: Array<{ id: string; name: string }>
  skills: string[]
}

interface RuleFormData {
  name: string
  status: boolean
  priority: number
  channels: string[]
  scopeSenderHandles: string
  maxAgents: number
  offerTimeoutSeconds: number
  targetAgentUserIds: string[]
  targetTeamIds: string[]
  targetSkills: string[]
  handoffMessage: string
  noAcceptMessage: string
}

const DEFAULT_HANDOFF_MSG =
  'Let me connect you with one of our agents. Please hold on a moment.'
const DEFAULT_NO_ACCEPT_MSG =
  'All our agents are busy right now. Your message is in the queue and an agent will reply as soon as possible.'

const EMPTY_FORM: RuleFormData = {
  name: '',
  status: true,
  priority: 100,
  channels: ['whatsapp', 'facebook', 'instagram', 'telegram'],
  scopeSenderHandles: '',
  maxAgents: 3,
  offerTimeoutSeconds: 15,
  targetAgentUserIds: [],
  targetTeamIds: [],
  targetSkills: [],
  handoffMessage: DEFAULT_HANDOFF_MSG,
  noAcceptMessage: DEFAULT_NO_ACCEPT_MSG,
}

const CHANNEL_OPTIONS = [
  { id: 'whatsapp', label: 'WhatsApp', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { id: 'facebook', label: 'Facebook Messenger', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { id: 'instagram', label: 'Instagram Direct', color: 'bg-pink-50 text-pink-700 border-pink-200' },
  { id: 'telegram', label: 'Telegram', color: 'bg-sky-50 text-sky-700 border-sky-200' },
  { id: 'chat', label: 'Live Web Chat', color: 'bg-purple-50 text-purple-700 border-purple-200' },
] as const

type StatusFilter = 'all' | 'active' | 'inactive'

// ---------------------------------------------------------------------------
// Searchable Agent Dropdown Component
// ---------------------------------------------------------------------------

interface SearchableTeamDropdownProps {
  teams: Array<{ id: string; name: string }>
  selectedTeamIds: string[]
  onChange: (teamIds: string[]) => void
  disabled?: boolean
}

function SearchableTeamDropdown({
  teams,
  selectedTeamIds,
  onChange,
  disabled = false,
}: SearchableTeamDropdownProps): JSX.Element {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  useEffect(() => {
    if (open) {
      searchInputRef.current?.focus()
    } else {
      setSearchQuery('')
    }
  }, [open])

  const q = searchQuery.trim().toLowerCase()
  const filteredTeams = q
    ? teams.filter((t) => t.name.toLowerCase().includes(q))
    : teams

  const toggleTeam = (teamId: string) => {
    if (selectedTeamIds.includes(teamId)) {
      onChange(selectedTeamIds.filter((id) => id !== teamId))
    } else {
      onChange([...selectedTeamIds, teamId])
    }
  }

  const selectAll = () => {
    onChange(teams.map((t) => t.id))
  }

  const clearAll = () => {
    onChange([])
  }

  let triggerText = 'Select teams (or leave empty for all teams)...'
  if (teams.length === 0) {
    triggerText = 'No teams available'
  } else if (selectedTeamIds.length === 0) {
    triggerText = 'All teams (click to select specific teams)'
  } else {
    triggerText = `${selectedTeamIds.length} of ${teams.length} team${teams.length !== 1 ? 's' : ''} selected`
  }

  const isInteractive = teams.length > 0 && !disabled
  const selectedTeamsList = teams.filter((t) => selectedTeamIds.includes(t.id))

  return (
    <div ref={containerRef} className="relative w-full space-y-1.5">
      <button
        type="button"
        disabled={!isInteractive}
        onClick={() => setOpen((prev) => !prev)}
        className={`flex w-full items-center justify-between rounded-sm border px-3 py-2 text-left text-xs transition-colors ${
          !isInteractive
            ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
            : open
            ? 'border-[#3454cf] ring-1 ring-[#3454cf] bg-white text-gray-800'
            : 'border-gray-300 bg-white text-gray-800 hover:border-gray-400'
        }`}
      >
        <span className="truncate flex items-center gap-1.5">
          <span>{triggerText}</span>
        </span>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Selected Team Badges / Chips */}
      {selectedTeamsList.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedTeamsList.map((t) => (
            <span
              key={t.id}
              className="inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700"
            >
              <span>{t.name}</span>
              <button
                type="button"
                onClick={() => toggleTeam(t.id)}
                className="hover:text-blue-900"
                title={`Remove ${t.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {open && isInteractive && (
        <div className="absolute left-0 right-0 top-full z-40 mt-1 max-h-64 rounded-sm border border-gray-200 bg-white shadow-lg overflow-hidden flex flex-col">
          <div className="border-b border-gray-100 p-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search teams by name..."
                className="w-full rounded-sm border border-gray-200 pl-8 pr-3 py-1.5 text-xs text-gray-900 placeholder:text-gray-400 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/70 px-3 py-1 text-[11px] text-gray-600">
            <span>{filteredTeams.length} team{filteredTeams.length !== 1 ? 's' : ''} available</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={selectAll}
                className="font-medium text-[#3454cf] hover:underline"
              >
                Select All
              </button>
              <button
                type="button"
                onClick={clearAll}
                className="text-gray-500 hover:underline"
              >
                Clear
              </button>
            </div>
          </div>

          <div className="max-h-44 overflow-y-auto divide-y divide-gray-50">
            {filteredTeams.length === 0 ? (
              <div className="p-4 text-center text-xs text-gray-500">
                No teams match &quot;{searchQuery}&quot;
              </div>
            ) : (
              filteredTeams.map((t) => {
                const checked = selectedTeamIds.includes(t.id)
                return (
                  <label
                    key={t.id}
                    className={`flex cursor-pointer items-center gap-2.5 px-3 py-2 text-xs transition-colors hover:bg-gray-50 ${
                      checked ? 'bg-blue-50/50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleTeam(t.id)}
                      className="h-3.5 w-3.5 rounded text-[#3454cf] focus:ring-0"
                    />
                    <span className="font-medium text-gray-800 truncate">{t.name}</span>
                  </label>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}

interface SearchableAgentDropdownProps {
  agents: Array<{ id: string; name: string; email: string }>
  selectedUserIds: string[]
  onChange: (userIds: string[]) => void
  disabled?: boolean
  loading?: boolean
  hasSelectedTeam: boolean
}

function SearchableAgentDropdown({
  agents,
  selectedUserIds,
  onChange,
  disabled = false,
  loading = false,
  hasSelectedTeam,
}: SearchableAgentDropdownProps): JSX.Element {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  useEffect(() => {
    if (open) {
      searchInputRef.current?.focus()
    } else {
      setSearchQuery('')
    }
  }, [open])

  const q = searchQuery.trim().toLowerCase()
  const filteredAgents = q
    ? agents.filter((a) => a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q))
    : agents

  const toggleAgent = (userId: string) => {
    if (selectedUserIds.includes(userId)) {
      onChange(selectedUserIds.filter((id) => id !== userId))
    } else {
      onChange([...selectedUserIds, userId])
    }
  }

  const selectAll = () => {
    onChange(agents.map((a) => a.id))
  }

  const clearAll = () => {
    onChange([])
  }

  let triggerText = 'Select agents from selected teams...'
  if (!hasSelectedTeam) {
    triggerText = 'Please select at least one team first'
  } else if (loading) {
    triggerText = 'Loading team members...'
  } else if (agents.length === 0) {
    triggerText = 'No agents found in selected team(s)'
  } else if (selectedUserIds.length === 0) {
    triggerText = 'All members from selected teams (click to pick specific agents)'
  } else {
    triggerText = `${selectedUserIds.length} of ${agents.length} agent${agents.length !== 1 ? 's' : ''} selected`
  }

  const isInteractive = hasSelectedTeam && !loading && agents.length > 0 && !disabled

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        disabled={!isInteractive}
        onClick={() => setOpen((prev) => !prev)}
        className={`flex w-full items-center justify-between rounded-sm border px-3 py-2 text-left text-xs transition-colors ${
          !isInteractive
            ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
            : open
            ? 'border-[#3454cf] ring-1 ring-[#3454cf] bg-white text-gray-800'
            : 'border-gray-300 bg-white text-gray-800 hover:border-gray-400'
        }`}
      >
        <span className="truncate flex items-center gap-1.5">
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
          <span>{triggerText}</span>
        </span>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && isInteractive && (
        <div className="absolute left-0 right-0 top-full z-40 mt-1 max-h-64 rounded-sm border border-gray-200 bg-white shadow-lg overflow-hidden flex flex-col">
          <div className="border-b border-gray-100 p-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search agents by name or email..."
                className="w-full rounded-sm border border-gray-200 pl-8 pr-3 py-1.5 text-xs text-gray-900 placeholder:text-gray-400 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/70 px-3 py-1 text-[11px] text-gray-600">
            <span>{filteredAgents.length} agent{filteredAgents.length !== 1 ? 's' : ''} available</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={selectAll}
                className="font-medium text-[#3454cf] hover:underline"
              >
                Select All
              </button>
              <button
                type="button"
                onClick={clearAll}
                className="text-gray-500 hover:underline"
              >
                Clear
              </button>
            </div>
          </div>

          <div className="max-h-44 overflow-y-auto divide-y divide-gray-50">
            {filteredAgents.length === 0 ? (
              <div className="p-4 text-center text-xs text-gray-500">
                No agents match &quot;{searchQuery}&quot;
              </div>
            ) : (
              filteredAgents.map((a) => {
                const checked = selectedUserIds.includes(a.id)
                return (
                  <label
                    key={a.id}
                    className={`flex cursor-pointer items-center gap-2.5 px-3 py-2 text-xs transition-colors hover:bg-gray-50 ${
                      checked ? 'bg-blue-50/50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleAgent(a.id)}
                      className="h-3.5 w-3.5 rounded text-[#3454cf] focus:ring-0"
                    />
                    <div className="flex flex-col truncate">
                      <span className="font-medium text-gray-800 truncate">{a.name}</span>
                      <span className="text-[10px] text-gray-400 truncate">{a.email}</span>
                    </div>
                  </label>
                )
              })
            )}
          </div>
        </div>
      )}

      {selectedUserIds.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {selectedUserIds.map((id) => {
            const agent = agents.find((a) => a.id === id)
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded bg-blue-50 border border-blue-200 px-2 py-0.5 text-[11px] font-medium text-blue-700"
              >
                <span>{agent ? agent.name : id}</span>
                <button
                  type="button"
                  onClick={() => toggleAgent(id)}
                  className="rounded-full p-0.5 hover:bg-blue-200 text-blue-600"
                  title="Remove agent"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function HandoffRulesPanel(): JSX.Element {
  const toast = useToast()
  const { confirm, Dialog } = useConfirmDialog()

  const [rules, setRules] = useState<HandoffRule[]>([])
  const [isHandoffEnabled, setIsHandoffEnabled] = useState(true)
  const [targets, setTargets] = useState<TargetAssignees>({ agents: [], teams: [], skills: [] })
  const [loading, setLoading] = useState(true)

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<HandoffRule | null>(null)
  const [form, setForm] = useState<RuleFormData>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [teamMembers, setTeamMembers] = useState<Array<{ id: string; name: string; email: string }>>([])
  const [loadingMembers, setLoadingMembers] = useState(false)

  // -------------------------------------------------------------------------
  // Data Loading
  // -------------------------------------------------------------------------

  const fetchTeamMembers = useCallback(
    async (teamIds: string[]) => {
      if (!teamIds || teamIds.length === 0) {
        setTeamMembers([])
        return
      }
      setLoadingMembers(true)
      try {
        const res = await api.get<{ members: Array<{ id: string; name: string; email: string }> }>(
          `/admin/ai/handoff-rules/teams/${teamIds.join(',')}/members`,
        )
        setTeamMembers(res.members ?? [])
      } catch (err) {
        toast.error('Failed to load team members', String(err))
        setTeamMembers([])
      } finally {
        setLoadingMembers(false)
      }
    },
    [toast],
  )

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [rulesRes, targetsRes] = await Promise.all([
        api.get<{ rules: HandoffRule[]; isHandoffEnabled: boolean }>('/admin/ai/handoff-rules'),
        api.get<TargetAssignees>('/admin/ai/handoff-rules/targets'),
      ])
      setRules(rulesRes.rules ?? [])
      setIsHandoffEnabled(rulesRes.isHandoffEnabled)
      setTargets(targetsRes ?? { agents: [], teams: [], skills: [] })
    } catch (err) {
      toast.error('Failed to load handoff rules', String(err))
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => {
    void loadData()
  }, [loadData])

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  function openCreate(): void {
    setEditing(null)
    setForm(EMPTY_FORM)
    setTeamMembers([])
    setDrawerOpen(true)
  }

  function openEdit(r: HandoffRule): void {
    setEditing(r)
    setForm({
      name: r.name,
      status: r.status,
      priority: r.priority,
      channels: r.channels,
      scopeSenderHandles: r.scopeSenderHandles.join(', '),
      maxAgents: r.maxAgents,
      offerTimeoutSeconds: r.offerTimeoutSeconds,
      targetAgentUserIds: r.targetAgentUserIds,
      targetTeamIds: r.targetTeamIds,
      targetSkills: r.targetSkills,
      handoffMessage: r.handoffMessage ?? '',
      noAcceptMessage: r.noAcceptMessage ?? '',
    })
    if (r.targetTeamIds && r.targetTeamIds.length > 0) {
      void fetchTeamMembers(r.targetTeamIds)
    } else {
      setTeamMembers([])
    }
    setDrawerOpen(true)
  }

  function handleTeamsSelect(teamIds: string[]): void {
    setForm((prev) => ({
      ...prev,
      targetTeamIds: teamIds,
    }))
    if (teamIds.length > 0) {
      void fetchTeamMembers(teamIds)
    } else {
      setTeamMembers([])
      setForm((prev) => ({ ...prev, targetAgentUserIds: [] }))
    }
  }

  async function handleToggle(r: HandoffRule): Promise<void> {
    try {
      await api.patch(`/admin/ai/handoff-rules/${r.id}/status`, { status: !r.status })
      setRules((prev) => prev.map((item) => (item.id === r.id ? { ...item, status: !r.status } : item)))
      toast.success(
        `Rule ${!r.status ? 'activated' : 'deactivated'}`,
        `"${r.name}" is now ${!r.status ? 'active' : 'inactive'}.`,
      )
    } catch (err) {
      toast.error('Failed to toggle status', String(err))
    }
  }

  async function handleDelete(r: HandoffRule): Promise<void> {
    confirm({
      title: 'Delete Handoff Rule',
      message: `Are you sure you want to delete "${r.name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        try {
          await api.delete(`/admin/ai/handoff-rules/${r.id}`)
          setRules((prev) => prev.filter((item) => item.id !== r.id))
          toast.success('Rule deleted', `Handoff rule "${r.name}" has been removed.`)
        } catch (err) {
          toast.error('Failed to delete rule', String(err))
        }
      },
    })
  }

  async function handleSave(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    if (!form.name.trim()) {
      toast.error('Name required', 'Please provide a rule name.')
      return
    }

    const handles = form.scopeSenderHandles
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)

    const payload = {
      name: form.name.trim(),
      status: form.status,
      priority: Number(form.priority) || 100,
      channels: form.channels,
      scopeSenderHandles: handles,
      maxAgents: Math.max(1, Math.min(20, Number(form.maxAgents) || 3)),
      offerTimeoutSeconds: Math.max(5, Math.min(120, Number(form.offerTimeoutSeconds) || 15)),
      targetAgentUserIds: form.targetAgentUserIds,
      targetTeamIds: form.targetTeamIds,
      targetSkills: form.targetSkills,
      handoffMessage: form.handoffMessage.trim() || null,
      noAcceptMessage: form.noAcceptMessage.trim() || null,
    }

    setSaving(true)
    try {
      if (editing) {
        const res = await api.put<{ rule: HandoffRule }>(`/admin/ai/handoff-rules/${editing.id}`, payload)
        setRules((prev) => prev.map((item) => (item.id === editing.id ? res.rule : item)))
        toast.success('Rule updated', `"${res.rule.name}" has been saved.`)
      } else {
        const res = await api.post<{ rule: HandoffRule }>('/admin/ai/handoff-rules', payload)
        setRules((prev) => [...prev, res.rule].sort((a, b) => a.priority - b.priority))
        toast.success('Rule created', `"${res.rule.name}" has been created.`)
      }
      setDrawerOpen(false)
    } catch (err) {
      toast.error('Failed to save rule', String(err))
    } finally {
      setSaving(false)
    }
  }

  // -------------------------------------------------------------------------
  // Filtering
  // -------------------------------------------------------------------------

  const filteredRules = rules.filter((r) => {
    if (statusFilter === 'active' && !r.status) return false
    if (statusFilter === 'inactive' && r.status) return false
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return (
      r.name.toLowerCase().includes(q) ||
      r.channels.some((c) => c.toLowerCase().includes(q)) ||
      r.scopeSenderHandles.some((h) => h.includes(q))
    )
  })

  // -------------------------------------------------------------------------
  // Render Helpers
  // -------------------------------------------------------------------------

  const allKnownAgents = [...targets.agents, ...teamMembers]
  const agentNameMap = new Map(allKnownAgents.map((a) => [a.id, a.name]))
  const teamNameMap = new Map(targets.teams.map((t) => [t.id, t.name]))

  function toggleChannel(c: string): void {
    setForm((prev) => ({
      ...prev,
      channels: prev.channels.includes(c)
        ? prev.channels.filter((x) => x !== c)
        : [...prev.channels, c],
    }))
  }

  function toggleTargetAgent(userId: string): void {
    setForm((prev) => ({
      ...prev,
      targetAgentUserIds: prev.targetAgentUserIds.includes(userId)
        ? prev.targetAgentUserIds.filter((id) => id !== userId)
        : [...prev.targetAgentUserIds, userId],
    }))
  }

  function toggleTargetTeam(teamId: string): void {
    setForm((prev) => ({
      ...prev,
      targetTeamIds: prev.targetTeamIds.includes(teamId)
        ? prev.targetTeamIds.filter((id) => id !== teamId)
        : [...prev.targetTeamIds, teamId],
    }))
  }

  function toggleTargetSkill(skillName: string): void {
    setForm((prev) => ({
      ...prev,
      targetSkills: prev.targetSkills.includes(skillName)
        ? prev.targetSkills.filter((s) => s !== skillName)
        : [...prev.targetSkills, skillName],
    }))
  }

  return (
    <div className="space-y-4">
      <Dialog />

      {/* Feature Flag Warning Banner */}
      {!isHandoffEnabled && (
        <div className="flex items-start gap-3 rounded-sm border border-amber-200 bg-amber-50 p-3.5 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="flex-1">
            <span className="font-semibold">AI Channel Handoff is switched off</span>
            <p className="mt-0.5 text-amber-700">
              The <code className="rounded bg-amber-100 px-1 py-0.5 font-mono text-[11px]">ai_handoff_channels</code> flag is explicitly disabled for this tenant, so an AI chat that cannot be answered is not offered to agents. You can still manage rules here; they take effect once the flag is enabled or its disabled row is removed.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">AI Chat Handoff Rules</h3>
          <p className="text-xs text-gray-500">
            Automatically transfer unanswerable AI chats to human agents on WhatsApp, Facebook, Instagram, and Web Chat
          </p>
        </div>
        <RequirePermission permission="ai:agents_manage" fallback={null}>
          <button
            className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0]"
            onClick={openCreate}
          >
            <Plus className="h-3.5 w-3.5" />
            Create Handoff Rule
          </button>
        </RequirePermission>
      </div>

      {/* List card */}
      <div className="overflow-hidden rounded-sm border border-gray-200 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-2.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by rule name, channels..."
              className="w-64 rounded-sm border border-gray-300 bg-white py-1.5 pl-8 pr-3 text-xs text-gray-700 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]"
            />
          </div>
          <div className="flex items-center gap-2">
            <Select
              ariaLabel="Status filter"
              size="sm"
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as StatusFilter)}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="px-4 py-2 font-medium">Priority</th>
                <th className="px-4 py-2 font-medium">Rule Name</th>
                <th className="px-4 py-2 font-medium">Channels</th>
                <th className="px-4 py-2 font-medium">Targets</th>
                <th className="px-4 py-2 font-medium">Offers & Timeout</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td className="px-4 py-8 text-center text-xs text-gray-400" colSpan={7}>
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-[#3454cf]" /> Loading handoff rules...
                    </span>
                  </td>
                </tr>
              ) : filteredRules.length === 0 ? (
                <tr>
                  <td className="px-4 py-10 text-center text-xs text-gray-400" colSpan={7}>
                    <ArrowRightLeft className="mx-auto mb-2 h-8 w-8 text-gray-300" />
                    {rules.length === 0
                      ? 'No AI handoff rules configured yet. The built-in default will be used.'
                      : 'No handoff rules match your search filter.'}
                  </td>
                </tr>
              ) : (
                filteredRules.map((r) => {
                  const targetAgentNames = r.targetAgentUserIds
                    .map((id) => agentNameMap.get(id))
                    .filter(Boolean) as string[]
                  const targetTeamNames = r.targetTeamIds
                    .map((id) => teamNameMap.get(id))
                    .filter(Boolean) as string[]

                  const totalTargets =
                    targetAgentNames.length + targetTeamNames.length + r.targetSkills.length

                  return (
                    <tr key={r.id} className="hover:bg-gray-50/50">
                      {/* Priority */}
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center rounded-sm bg-gray-100 px-2 py-0.5 font-mono text-[11px] font-semibold text-gray-700">
                          #{r.priority}
                        </span>
                      </td>

                      {/* Name + Scope */}
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{r.name}</div>
                        {r.scopeSenderHandles.length > 0 && (
                          <div className="mt-0.5 text-[11px] text-gray-500">
                            Scope: {r.scopeSenderHandles.join(', ')}
                          </div>
                        )}
                      </td>

                      {/* Channels */}
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {r.channels.length === 0 ? (
                            <span className="inline-block rounded px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600">
                              All Channels (except Chat)
                            </span>
                          ) : (
                            r.channels.map((c) => {
                              const meta = CHANNEL_OPTIONS.find((opt) => opt.id === c)
                              return (
                                <span
                                  key={c}
                                  className={`inline-block rounded border px-1.5 py-0.5 text-[10px] font-medium ${meta?.color ?? 'bg-gray-100 text-gray-600 border-gray-200'}`}
                                >
                                  {meta?.label ?? c}
                                </span>
                              )
                            })
                          )}
                        </div>
                      </td>

                      {/* Targets */}
                      <td className="px-4 py-3">
                        {totalTargets === 0 ? (
                          <span className="text-[11px] text-gray-400 italic">
                            Any online agent
                          </span>
                        ) : (
                          <div className="space-y-0.5 text-[11px] text-gray-600">
                            {targetTeamNames.length > 0 && (
                              <div>
                                <span className="font-medium text-gray-700">Teams:</span>{' '}
                                {targetTeamNames.join(', ')}
                              </div>
                            )}
                            {targetAgentNames.length > 0 && (
                              <div>
                                <span className="font-medium text-gray-700">Agents:</span>{' '}
                                {targetAgentNames.join(', ')}
                              </div>
                            )}
                            {r.targetSkills.length > 0 && (
                              <div>
                                <span className="font-medium text-gray-700">Skills:</span>{' '}
                                {r.targetSkills.join(', ')}
                              </div>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Offers & Timeout */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 text-gray-700">
                          <Clock className="h-3.5 w-3.5 text-gray-400" />
                          <span>
                            {r.maxAgents} {r.maxAgents === 1 ? 'agent' : 'agents'} · {r.offerTimeoutSeconds}s popup
                          </span>
                        </div>
                      </td>

                      {/* Status Toggle */}
                      <td className="px-4 py-3">
                        <label className="relative inline-flex cursor-pointer items-center">
                          <input
                            type="checkbox"
                            checked={r.status}
                            onChange={() => handleToggle(r)}
                            className="peer sr-only"
                          />
                          <div className="peer h-4 w-7 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-3 after:w-3 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-[#3454cf] peer-checked:after:translate-x-full peer-focus:outline-none" />
                          <span className="ml-2 text-[11px] text-gray-600">
                            {r.status ? 'Active' : 'Inactive'}
                          </span>
                        </label>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <RequirePermission permission="ai:agents_manage" fallback={null}>
                            <button
                              className="rounded-sm p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                              onClick={() => openEdit(r)}
                              title="Edit Rule"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              className="rounded-sm p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600"
                              onClick={() => handleDelete(r)}
                              title="Delete Rule"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </RequirePermission>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit Drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div
            className="absolute right-0 top-0 flex h-full w-full flex-col border-l border-gray-200 bg-white shadow-2xl md:w-[600px]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drawer Header */}
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3.5">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-blue-50 text-[#3454cf]">
                  <ArrowRightLeft className="h-4 w-4" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-gray-900">
                    {editing ? `Edit Rule: ${editing.name}` : 'New AI Handoff Rule'}
                  </h2>
                  <p className="text-[11px] text-gray-500">
                    Transfer chat to human agents when AI cannot assist
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 cursor-pointer text-xs font-medium text-gray-700">
                  <span>{form.status ? 'Active' : 'Inactive'}</span>
                  <div className="relative inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.checked })}
                      className="peer sr-only"
                    />
                    <div className="peer h-4 w-7 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-3 after:w-3 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-[#3454cf] peer-checked:after:translate-x-full peer-focus:outline-none" />
                  </div>
                </label>
                <button
                  className="rounded-sm p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  onClick={() => setDrawerOpen(false)}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Drawer Form Body */}
            <form onSubmit={handleSave} className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Rule Name */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Rule Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. VIP Support Handoff"
                  className="w-full rounded-sm border border-gray-300 px-3 py-2 text-xs text-gray-900 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]"
                />
              </div>

              {/* Channels */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-medium text-gray-700">Trigger Channels</label>
                  <span className="text-[11px] text-gray-400">
                    {form.channels.length === 0 ? 'All channels (default)' : `${form.channels.length} selected`}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {CHANNEL_OPTIONS.map((opt) => {
                    const checked = form.channels.includes(opt.id)
                    return (
                      <button
                        type="button"
                        key={opt.id}
                        onClick={() => toggleChannel(opt.id)}
                        className={`flex items-center justify-center gap-1.5 rounded-sm border py-2 px-2.5 text-xs font-medium transition-colors ${
                          checked
                            ? `${opt.color} border-current ring-1 ring-current/20`
                            : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {}}
                          className="h-3.5 w-3.5 rounded text-[#3454cf] focus:ring-0"
                        />
                        <span>{opt.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Agent Assignment */}
              <div className="rounded-sm border border-gray-200 bg-gray-50/50 p-3.5 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-800">
                    <Users className="h-4 w-4 text-[#3454cf]" />
                    <span>Agent Assignment</span>
                  </div>
                  <span className="text-[11px] text-gray-400">Leave empty for any online agent</span>
                </div>

                {/* Target Teams Dropdown with Search & Checkboxes */}
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                    Target Teams ({form.targetTeamIds.length} selected)
                  </label>
                  <SearchableTeamDropdown
                    teams={targets.teams}
                    selectedTeamIds={form.targetTeamIds}
                    onChange={handleTeamsSelect}
                  />
                </div>

                {/* Target Agents Dropdown with Search */}
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                    Target Agents ({form.targetAgentUserIds.length} selected)
                  </label>
                  <SearchableAgentDropdown
                    agents={teamMembers}
                    selectedUserIds={form.targetAgentUserIds}
                    onChange={(ids) => setForm((prev) => ({ ...prev, targetAgentUserIds: ids }))}
                    loading={loadingMembers}
                    hasSelectedTeam={form.targetTeamIds.length > 0}
                  />
                </div>

                {/* Skills Multi-select */}
                {targets.skills.length > 0 && (
                  <div>
                    <label className="block text-[11px] font-medium text-gray-700 mb-1">
                      Required Skills ({form.targetSkills.length} selected)
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {targets.skills.map((s) => {
                        const checked = form.targetSkills.includes(s)
                        return (
                          <button
                            type="button"
                            key={s}
                            onClick={() => toggleTargetSkill(s)}
                            className={`rounded-full px-2.5 py-0.5 text-xs font-medium border transition-colors ${
                              checked
                                ? 'bg-indigo-50 text-indigo-700 border-indigo-300'
                                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            {checked ? '✓ ' : '+ '}
                            {s}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Routing Controls */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10000"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value, 10) || 100 })}
                    placeholder="100"
                    className="w-full rounded-sm border border-gray-300 px-3 py-1.5 text-xs text-gray-900 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]"
                  />
                  <p className="mt-0.5 text-[10px] text-gray-400">Lower runs first</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Max Agents
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={form.maxAgents}
                    onChange={(e) => setForm({ ...form, maxAgents: parseInt(e.target.value, 10) || 3 })}
                    placeholder="3"
                    className="w-full rounded-sm border border-gray-300 px-3 py-1.5 text-xs text-gray-900 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]"
                  />
                  <p className="mt-0.5 text-[10px] text-gray-400">1 to 20 agents</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Timeout (sec)
                  </label>
                  <input
                    type="number"
                    min="5"
                    max="120"
                    value={form.offerTimeoutSeconds}
                    onChange={(e) => setForm({ ...form, offerTimeoutSeconds: parseInt(e.target.value, 10) || 15 })}
                    placeholder="15"
                    className="w-full rounded-sm border border-gray-300 px-3 py-1.5 text-xs text-gray-900 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]"
                  />
                  <p className="mt-0.5 text-[10px] text-gray-400">Ring duration (5–120s)</p>
                </div>
              </div>

              {/* Customer Scope */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Customer Scope <span className="font-normal text-gray-400">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={form.scopeSenderHandles}
                  onChange={(e) => setForm({ ...form, scopeSenderHandles: e.target.value })}
                  placeholder="All customers (or comma-separated phone numbers / IDs)"
                  className="w-full rounded-sm border border-gray-300 px-3 py-1.5 text-xs text-gray-900 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]"
                />
              </div>

              {/* Customer Messages */}
              <div className="space-y-3 rounded-sm border border-gray-200 bg-white p-3.5">
                <span className="text-xs font-semibold text-gray-800">Customer Messages</span>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[11px] font-medium text-gray-700">
                      Handoff Notice
                    </label>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, handoffMessage: DEFAULT_HANDOFF_MSG })}
                      className="text-[10px] text-[#3454cf] hover:underline"
                    >
                      Reset default
                    </button>
                  </div>
                  <textarea
                    rows={2}
                    value={form.handoffMessage}
                    onChange={(e) => setForm({ ...form, handoffMessage: e.target.value })}
                    placeholder={DEFAULT_HANDOFF_MSG}
                    className="w-full rounded-sm border border-gray-300 p-2 text-xs text-gray-900 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[11px] font-medium text-gray-700">
                      Agents Busy Notice
                    </label>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, noAcceptMessage: DEFAULT_NO_ACCEPT_MSG })}
                      className="text-[10px] text-[#3454cf] hover:underline"
                    >
                      Reset default
                    </button>
                  </div>
                  <textarea
                    rows={2}
                    value={form.noAcceptMessage}
                    onChange={(e) => setForm({ ...form, noAcceptMessage: e.target.value })}
                    placeholder={DEFAULT_NO_ACCEPT_MSG}
                    className="w-full rounded-sm border border-gray-300 p-2 text-xs text-gray-900 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]"
                  />
                </div>
              </div>

              {/* Drawer Footer Actions */}
              <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={() => setDrawerOpen(false)}
                  className="rounded-sm border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0] disabled:opacity-50"
                >
                  {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {editing ? 'Save Changes' : 'Create Rule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
